/**
 * Preview runner for the add-tile dialog (S6). Runs the SQL through the ONE
 * fetch path (`startTileExecution`) under `trigger: 'preview'` — main-side
 * policy makes previews ALWAYS read-only; a write statement comes back as
 * `blockedByPolicy`, which the dialog renders as the "Writes ⚠" trust-strip
 * state rather than an error. Durations accumulate to seed the 5×-median
 * refresh guardrail before the tile exists.
 */
import { useCallback, useRef, useState } from 'react';
import type { ColumnTypeInfo } from '../../../charts/types/chartTypes';
import { analyzeColumns } from '../../../charts/utils/columnTypeDetection';
import type { TableTile } from '../../types/dashboardTypes';
import type { TileRunResult } from '../../types/dashboardRuntime';
import { startTileExecution } from '../../services/tileExecutionService';

export type TilePreviewState =
  | { status: 'idle' }
  | { status: 'running' }
  | { status: 'done'; result: TileRunResult & { ok: true } }
  | { status: 'blocked'; message: string }
  | { status: 'error'; message: string };

export type TilePreview = {
  state: TilePreviewState;
  /** Rolling preview durations — seeds the trust-strip guardrail median. */
  durationsMs: number[];
  run: (args: { connectionId: string; database?: string; sql: string; dashboardId: string }) => void;
  reset: () => void;
};

export function useTilePreview(): TilePreview {
  const [state, setState] = useState<TilePreviewState>({ status: 'idle' });
  const [durationsMs, setDurationsMs] = useState<number[]>([]);
  const runSeq = useRef(0);
  const lastSql = useRef<string | null>(null);

  const run = useCallback(
    (args: { connectionId: string; database?: string; sql: string; dashboardId: string }) => {
      const seq = ++runSeq.current;
      // The guardrail median only makes sense across runs of the SAME query —
      // a different statement has a different duration profile.
      if (lastSql.current !== args.sql) {
        lastSql.current = args.sql;
        setDurationsMs([]);
      }
      setState({ status: 'running' });
      // Ephemeral query-backed tile — never persisted; only connection,
      // database and SQL matter to the request.
      const tile: TableTile = {
        id: `preview-${seq}`,
        tileType: 'table',
        title: 'Preview',
        layout: { x: 0, y: 0, w: 1, h: 1 },
        connectionId: args.connectionId,
        database: args.database,
        source: { kind: 'sql', sql: args.sql },
        refresh: { mode: 'manual' },
        rowLimit: 1000,
        density: 'compact',
      };
      const { promise } = startTileExecution(tile, null, [], {
        trigger: 'preview',
        dashboardId: args.dashboardId,
      });
      void promise.then((result) => {
        if (seq !== runSeq.current) return; // superseded by a newer preview
        if (result.ok) {
          setDurationsMs((prev) => [...prev.slice(-9), result.durationMs]);
          setState({ status: 'done', result });
        } else if (result.blockedByPolicy) {
          setState({ status: 'blocked', message: result.blockedByPolicy });
        } else {
          setState({ status: 'error', message: result.error });
        }
      });
    },
    [],
  );

  const reset = useCallback(() => {
    runSeq.current++;
    lastSql.current = null;
    setState({ status: 'idle' });
    setDurationsMs([]);
  }, []);

  return { state, durationsMs, run, reset };
}

/** Column shape of the picked preview set — feeds the config sections. */
export function pickedColumnInfos(preview: TilePreview, resultSetIndex: number): ColumnTypeInfo[] {
  if (preview.state.status !== 'done') return [];
  const picked =
    preview.state.result.resultSets[resultSetIndex] ?? preview.state.result.resultSets[0];
  if (!picked) return [];
  return analyzeColumns(picked.columns, picked.previewRows as Array<Record<string, unknown>>);
}
